/* Noctera theme script. No dependencies; every feature degrades to plain HTML without it. */
(() => {
  const root = window.Shopify?.routes?.root || '/';

  /* Mobile menu: close button inside the <details> drawer */
  document.addEventListener('click', (event) => {
    const close = event.target.closest('[data-drawer-close]');
    if (close) close.closest('details')?.removeAttribute('open');
  });

  /* Cart drawer */
  const drawer = document.querySelector('[data-cart-drawer]');
  let lastFocus = null;

  const openCart = () => {
    if (!drawer) return false;
    lastFocus = document.activeElement;
    drawer.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    drawer.querySelector('[role="dialog"]')?.focus();
    return true;
  };
  const closeCart = () => {
    if (!drawer || drawer.hidden) return;
    drawer.hidden = true;
    document.documentElement.style.overflow = '';
    lastFocus?.focus?.();
  };

  const renderCart = (sections) => {
    const html = sections?.['cart-drawer'];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const body = doc.querySelector('[data-cart-body]');
    document.querySelectorAll('[data-cart-body]').forEach((el) => {
      el.replaceChildren(...[...body.childNodes].map((node) => document.importNode(node, true)));
    });
    const count = doc.querySelector('[data-cart-count-text]');
    document.querySelectorAll('[data-cart-count-text]').forEach((el) => { el.textContent = count.textContent; });
    const n = Number((count.textContent.match(/\d+/) || [0])[0]);
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = n; el.hidden = n === 0; });
  };

  /* Apps like Kaching rewrite /cart/add requests and may drop the sections parameter */
  const cartSections = async (data) => data?.sections || (await (await fetch(`${root}?sections=cart-drawer`)).json());

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-open]') && openCart()) event.preventDefault();
    if (event.target.closest('[data-cart-close]')) {
      if (event.target.closest('a[href]') && !drawer?.contains(event.target)) return;
      if (drawer?.contains(event.target)) event.preventDefault();
      closeCart();
    }
  });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeCart(); });

  /* Quantity changes in drawer and cart page */
  const changeLine = async (line, quantity, scope) => {
    scope.classList.add('is-loading');
    try {
      const res = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-drawer'] }),
      });
      const data = await res.json();
      renderCart(await cartSections(data));
    } finally {
      scope.classList.remove('is-loading');
    }
  };
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-qty]');
    if (!btn) return;
    const line = btn.closest('[data-line]');
    if (!line) return;
    changeLine(Number(line.dataset.line), Math.max(0, Number(btn.dataset.qty)), btn.closest('[data-cart-body]'));
  });
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-qty-input]');
    if (!input) return;
    const line = input.closest('[data-line]');
    changeLine(Number(line.dataset.line), Math.max(0, Number(input.value)), input.closest('[data-cart-body]'));
  });

  /* Add to cart */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !drawer) return;
    event.preventDefault();
    const buttons = [form.querySelector('[data-add-button]'), ...document.querySelectorAll(`[form="${form.id}"]`)].filter(Boolean);
    const error = form.querySelector('[data-form-error]');
    buttons.forEach((b) => { b.disabled = true; b.classList.add('is-loading'); });
    if (error) error.hidden = true;
    try {
      const body = new FormData(form);
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
      const res = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Das hat nicht geklappt.');
      renderCart(await cartSections(data));
      openCart();
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; }
    } finally {
      buttons.forEach((b) => { b.disabled = false; b.classList.remove('is-loading'); });
    }
  });

  /* Variant select keeps the hidden id in sync */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    select.form.querySelector('[data-variant-id]').value = select.value;
  });

  /* Gallery: scroll-snap track with thumbs and arrows */
  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-gallery-track]');
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const count = track.children.length;
    const index = () => Math.round(track.scrollLeft / track.clientWidth);
    const go = (i) => track.scrollTo({ left: ((i + count) % count) * track.clientWidth, behavior: 'smooth' });
    thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.galleryThumb))));
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => go(index() - 1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => go(index() + 1));
    let frame;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const i = index();
        thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
        thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }, { passive: true });
  });

  /* Delivery window in working days (Mon to Fri) */
  const addWorkdays = (date, days) => {
    const d = new Date(date);
    let left = days;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) left -= 1;
    }
    return d;
  };
  const fmt = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  const dayName = new Intl.DateTimeFormat('de-DE', { weekday: 'long' });
  document.querySelectorAll('[data-delivery]').forEach((el) => {
    const now = new Date();
    const from = fmt.format(addWorkdays(now, Number(el.dataset.min)));
    const to = fmt.format(addWorkdays(now, Number(el.dataset.max)));
    const target = el.querySelector('[data-delivery-text]');
    target.textContent = el.dataset.template.replace('{von}', from).replace('{bis}', to);
  });

  /* Order countdown: time left until the delivery window moves by a working day.
     Friday to Sunday share one window, so the deadline can be days away. */
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  document.querySelectorAll('[data-urgency]').forEach((el) => {
    const min = Number(el.dataset.min);
    const max = Number(el.dataset.max);
    const title = el.querySelector('[data-urgency-title]');
    const text = el.querySelector('[data-urgency-text]');
    const render = () => {
      const now = new Date();
      const from = addWorkdays(now, min);
      const deadline = new Date(now);
      deadline.setHours(24, 0, 0, 0);
      while (sameDay(addWorkdays(deadline, min), from)) deadline.setDate(deadline.getDate() + 1);
      const left = deadline - now;
      const d = Math.floor(left / 864e5);
      const h = Math.floor((left % 864e5) / 36e5);
      const m = Math.floor((left % 36e5) / 6e4);
      const s = Math.floor((left % 6e4) / 1e3);
      const countdown = `${d ? `${d} Tg. ` : ''}${h} Std. ${m} Min. ${s} Sek.`;
      const lastDay = new Date(deadline - 1);
      const until = sameDay(lastDay, now) ? 'heute bis 23:59 Uhr' : `bis ${dayName.format(lastDay)}, 23:59 Uhr`;
      const fill = (tpl) => tpl
        .replace('{countdown}', `<strong class="urgency__count">${countdown}</strong>`)
        .replace('{deadline}', until)
        .replace('{von}', fmt.format(from))
        .replace('{bis}', fmt.format(addWorkdays(now, max)));
      title.innerHTML = fill(title.dataset.template);
      text.innerHTML = fill(text.dataset.template);
      el.hidden = false;
    };
    render();
    setInterval(render, 1000);
  });

  /* Lower buy buttons and the sticky bar: with a bundle picker on the page they scroll to it,
     so nobody adds a bundle they did not see. Without one they submit the main form. */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-buy-now], [data-sticky-add]');
    if (!trigger) return;
    const picker = document.querySelector('kaching-bundle[data-initialized="true"]');
    if (picker && picker.offsetHeight > 0) {
      event.preventDefault();
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (trigger.matches('[data-buy-now]')) document.querySelector('[data-product-form]')?.requestSubmit();
  });

  /* Review list: reveal the rest */
  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-reviews-more]');
    if (!more) return;
    more.closest('.reviews')?.querySelectorAll('[data-review-more]').forEach((li) => { li.hidden = false; });
    more.parentElement.remove();
  });

  /* Review carousel arrows */
  document.querySelectorAll('[data-carousel]').forEach((track) => {
    const wrap = track.parentElement;
    const step = () => (track.firstElementChild?.getBoundingClientRect().width || 260) + 16;
    wrap.querySelector('[data-carousel-prev]')?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    wrap.querySelector('[data-carousel-next]')?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
  });

  /* Sticky buy bar: visible once the main button has scrolled away */
  const sticky = document.querySelector('[data-sticky-buy]');
  const mainButton = document.querySelector('[data-add-button]');
  if (sticky && mainButton && 'IntersectionObserver' in window) {
    sticky.hidden = false;
    const footer = document.querySelector('.footer');
    let pastButton = false;
    let footerVisible = false;
    const update = () => sticky.classList.toggle('is-visible', pastButton && !footerVisible);
    new IntersectionObserver(([entry]) => {
      pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      update();
    }).observe(mainButton);
    if (footer) new IntersectionObserver(([entry]) => { footerVisible = entry.isIntersecting; update(); }).observe(footer);
  }
})();
